/**
 * Structured template values — the slash-native replacement for `{{ }}` strings.
 *
 * A templated field is stored as an ordered array of segments instead of a
 * string with embedded `{{Header}}` tokens. This file is the single shared
 * engine every module uses to read, resolve, and analyse those values. It is
 * pure, server-safe, and dependency-free, so it can be BYTE-IDENTICAL in
 * proly-frontend and proly-backend.
 *
 *   value: [
 *     { t: "field", name: "Color", origin: "source" },
 *     { t: "text",  v: " - " },
 *     { t: "field", name: "Title", origin: "source", pipes: [{ name: "upper" }] },
 *   ]
 *
 * `{{ }}` is gone from storage and from the UI. The only place a legacy
 * `{{Header}}` / `{{out:Field}}` string is ever touched is `parseLegacy`, kept
 * as a read-only safety net (and used by the one-shot DB migration). Nothing
 * here ever throws — bad input degrades to a text segment / empty string.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A field reference's provenance.
 *  - "source"   — a column from the uploaded file (was a bare `{{Name}}`).
 *  - "workbook" — an already-processed output column (was `{{out:Name}}`).
 *  - "computed" — a reserved engine token, e.g. price-engine's `subtotal`. */
export type SegmentOrigin = "source" | "workbook" | "computed";

/** A pipe helper applied to a field value, e.g. `{ name: "default", args: ["--"] }`. */
export type Pipe = { name: string; args?: string[] };

export type Segment =
  | { t: "text"; v: string }
  | { t: "field"; name: string; origin: SegmentOrigin; pipes?: Pipe[] };

/** The structured value stored for any templated field. */
export type TemplateValue = Segment[];

/** Reserved computed token names (resolved from the `computed` context, not a row). */
export const COMPUTED_NAMES = new Set(["subtotal"]);

/** Legacy prefix that flagged an output-cell reference inside `{{ }}`. */
export const LEGACY_OUTPUT_REF_PREFIX = "out:";

// ---------------------------------------------------------------------------
// Type guards / coercion
// ---------------------------------------------------------------------------

function isSegment(x: unknown): x is Segment {
  if (!x || typeof x !== "object") return false;
  const s = x as { t?: unknown };
  if (s.t === "text") return typeof (x as { v?: unknown }).v === "string";
  if (s.t === "field") return typeof (x as { name?: unknown }).name === "string";
  return false;
}

/** True when `v` is already a structured TemplateValue (array of segments). */
export function isTemplateValue(v: unknown): v is TemplateValue {
  return Array.isArray(v) && v.every(isSegment);
}

/**
 * Accept either a new structured value or a legacy `{{ }}` string and always
 * return a TemplateValue. This is the additive bridge that keeps old Profile
 * JSON working: a stored string is parsed on read; a stored array is used as-is.
 */
export function toTemplateValue(
  v: string | TemplateValue | null | undefined,
): TemplateValue {
  if (v == null) return [];
  if (typeof v === "string") return parseLegacy(v);
  if (isTemplateValue(v)) return v;
  return [];
}

// ---------------------------------------------------------------------------
// Legacy parsing  ({{Header}} / {{out:Field}} / {{x | upper}})  →  segments
// ---------------------------------------------------------------------------

/**
 * Convert a legacy `{{ }}` template string into structured segments. Tolerant:
 * an unclosed `{{` (or any malformed token) is emitted as literal text rather
 * than throwing. This is the ONLY function that understands `{{ }}` syntax.
 */
export function parseLegacy(tpl: string): TemplateValue {
  const out: TemplateValue = [];
  let text = "";
  let i = 0;

  const flushText = () => {
    if (text.length > 0) {
      out.push({ t: "text", v: text });
      text = "";
    }
  };

  while (i < tpl.length) {
    if (tpl[i] === "{" && tpl[i + 1] === "{") {
      const closeIdx = tpl.indexOf("}}", i + 2);
      if (closeIdx === -1) {
        // Unclosed — the remainder is literal text.
        text += tpl.slice(i);
        break;
      }
      const inner = tpl.slice(i + 2, closeIdx);
      i = closeIdx + 2;
      const seg = legacyTokenToSegment(inner);
      if (seg) {
        flushText();
        out.push(seg);
      } else {
        // Empty token like `{{}}` or `{{   }}` — drop silently (matches old "").
      }
    } else {
      text += tpl[i];
      i++;
    }
  }
  flushText();
  return out;
}

function legacyTokenToSegment(inner: string): Segment | null {
  const parts = splitPipes(inner);
  let head = parts[0].trim();
  if (!head) return null;

  let origin: SegmentOrigin = "source";
  if (head.toLowerCase().startsWith(LEGACY_OUTPUT_REF_PREFIX)) {
    origin = "workbook";
    head = head.slice(LEGACY_OUTPUT_REF_PREFIX.length).trim();
  } else if (COMPUTED_NAMES.has(head.toLowerCase())) {
    origin = "computed";
    head = head.toLowerCase();
  }
  if (!head) return null;

  const pipes: Pipe[] = [];
  for (let p = 1; p < parts.length; p++) {
    const expr = parts[p].trim();
    if (!expr) continue;
    pipes.push(parsePipeExpr(expr));
  }

  const seg: Segment = { t: "field", name: head, origin };
  if (pipes.length) seg.pipes = pipes;
  return seg;
}

/**
 * Convert a legacy "input" field (dictionary / find-replace / dropbox-image)
 * into segments. These fields had DUAL semantics a plain template doesn't:
 * a bare string was a column reference (not literal text), `out:Field` a
 * workbook reference, and only an embedded `{{ }}` was a template. Used by the
 * migration so a bare-header input becomes a field segment, not text.
 */
export function parseLegacyInput(str: string): TemplateValue {
  const s = (str ?? "").trim();
  if (!s) return [];
  if (s.includes("{{")) return parseLegacy(str);
  if (s.toLowerCase().startsWith(LEGACY_OUTPUT_REF_PREFIX)) {
    return [{ t: "field", name: s.slice(LEGACY_OUTPUT_REF_PREFIX.length).trim(), origin: "workbook" }];
  }
  return [{ t: "field", name: s, origin: "source" }];
}

// ---------------------------------------------------------------------------
// Resolution  (segments → string)
// ---------------------------------------------------------------------------

export type ResolveContext = {
  /** Source row (uploaded file columns). */
  row: Record<string, unknown>;
  /** Partial output row, for workbook (`origin: "workbook"`) references. */
  output?: Record<string, string>;
  /** Reserved computed tokens, e.g. `{ subtotal: "1234.50" }`. */
  computed?: Record<string, string>;
  /** Pipe helpers. `(value, ...args) => string`. Caller merges defaults. */
  helpers?: Record<string, (value: string, ...args: string[]) => string>;
};

/**
 * Resolve a structured value (or a legacy string) against a row. Mirrors the
 * old `expand()` semantics exactly: missing values → "", unknown helpers pass
 * the value through, and it never throws.
 */
export function resolveTemplate(
  value: string | TemplateValue | null | undefined,
  ctx: ResolveContext,
): string {
  const segments = toTemplateValue(value);
  const helpers = ctx.helpers ?? {};
  const warnedUnknown = new Set<string>();
  let result = "";

  for (const seg of segments) {
    if (seg.t === "text") {
      result += seg.v;
      continue;
    }
    let v: string;
    if (seg.origin === "workbook") {
      v = ctx.output ? (ctx.output[seg.name] ?? "") : "";
    } else if (seg.origin === "computed") {
      v = ctx.computed ? (ctx.computed[seg.name] ?? "") : "";
    } else {
      v = cellToString(ctx.row[seg.name]);
    }
    for (const pipe of seg.pipes ?? []) {
      const fn = helpers[pipe.name];
      if (!fn) {
        if (!warnedUnknown.has(pipe.name)) {
          console.warn(`[templateValue] Unknown helper "${pipe.name}" — passing value through.`);
          warnedUnknown.add(pipe.name);
        }
        continue;
      }
      v = fn(v, ...(pipe.args ?? []));
    }
    result += v;
  }
  return result;
}

/**
 * Resolve an "input" field value. A structured value resolves normally; a
 * legacy STRING keeps the dual column-reference semantics (bare header / out:
 * / `{{template}}`) so un-migrated rules still read the right column.
 */
export function resolveInputValue(
  input: string | TemplateValue | null | undefined,
  ctx: ResolveContext,
): string {
  if (input == null) return "";
  if (typeof input !== "string") return resolveTemplate(input, ctx);
  if (input.includes("{{")) return resolveTemplate(input, ctx);
  const key = input.trim();
  if (key.toLowerCase().startsWith(LEGACY_OUTPUT_REF_PREFIX)) {
    return ctx.output ? (ctx.output[key.slice(LEGACY_OUTPUT_REF_PREFIX.length).trim()] ?? "") : "";
  }
  return cellToString(ctx.row[key]);
}

/**
 * Build a formula string for the expression/price evaluators. Text segments
 * (operators, literals) pass through verbatim; a `computed` field (subtotal)
 * becomes an unquoted `(value)` numeric group; every other field becomes a
 * JSON string literal so the evaluator coerces it safely. Works for both
 * structured values and legacy `{{ }}` / `{{subtotal}}` strings.
 */
export function substituteExpression(
  value: string | TemplateValue | null | undefined,
  ctx: ResolveContext,
): string {
  const segments = toTemplateValue(value);
  let out = "";
  for (const seg of segments) {
    if (seg.t === "text") {
      out += seg.v;
    } else if (seg.origin === "computed") {
      const raw = ctx.computed?.[seg.name] ?? "0";
      out += `(${raw})`;
    } else {
      out += JSON.stringify(resolveTemplate([seg], ctx));
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Dependency analysis  (segments → referenced fields)
// ---------------------------------------------------------------------------

export type FieldRef = { name: string; origin: SegmentOrigin };

/**
 * Every field reference in a value, in document order, de-duplicated by
 * origin+name. Replaces the old `{{out:...}}` regex used for dependency
 * ordering — callers that only care about workbook deps filter on origin.
 */
export function extractFieldRefs(
  value: string | TemplateValue | null | undefined,
): FieldRef[] {
  const segments = toTemplateValue(value);
  const refs: FieldRef[] = [];
  const seen = new Set<string>();
  for (const seg of segments) {
    if (seg.t !== "field") continue;
    if (!seg.name) continue;
    const key = `${seg.origin}:${seg.name}`;
    if (seen.has(key)) continue;
    seen.add(key);
    refs.push({ name: seg.name, origin: seg.origin });
  }
  return refs;
}

// ---------------------------------------------------------------------------
// Internal — ported verbatim from the old template.ts (pipe + cell helpers)
// ---------------------------------------------------------------------------

/** Split on `|` at top-level only (not inside single/double quotes). */
function splitPipes(s: string): string[] {
  const parts: string[] = [];
  let current = "";
  let inSingle = false;
  let inDouble = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === "'" && !inDouble) {
      inSingle = !inSingle;
      current += ch;
    } else if (ch === '"' && !inSingle) {
      inDouble = !inDouble;
      current += ch;
    } else if (ch === "|" && !inSingle && !inDouble) {
      parts.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  parts.push(current);
  return parts;
}

/** Parse `upper` / `default:"--"` / `default:"a","b"` into `{ name, args }`. */
function parsePipeExpr(expr: string): Pipe {
  const colonIdx = expr.indexOf(":");
  if (colonIdx === -1) return { name: expr.trim() };
  const name = expr.slice(0, colonIdx).trim();
  const argStr = expr.slice(colonIdx + 1).trim();
  const args: string[] = [];
  let i = 0;
  while (i < argStr.length) {
    if (argStr[i] === "," || argStr[i] === " ") {
      i++;
      continue;
    }
    if (argStr[i] === '"' || argStr[i] === "'") {
      const quote = argStr[i];
      i++;
      let arg = "";
      while (i < argStr.length && argStr[i] !== quote) {
        arg += argStr[i];
        i++;
      }
      i++;
      args.push(arg);
    } else {
      let arg = "";
      while (i < argStr.length && argStr[i] !== ",") {
        arg += argStr[i];
        i++;
      }
      args.push(arg.trim());
    }
  }
  return args.length ? { name, args } : { name };
}

/** Convert a source cell to a string for substitution (null/Date/number/etc). */
function cellToString(cell: unknown): string {
  if (cell == null) return "";
  if (cell instanceof Date) return cell.toISOString();
  return String(cell);
}
